if (!customElements.get("simple-tabs")) {
  class SimpleTabs extends HTMLElement {
    constructor() {
      super();
      this.bars = [...this.querySelectorAll("[data-bar]")];
      this.tabs = [...this.querySelectorAll("[data-content]")];

      this.sidebar = this.querySelector("[data-sidebar]");
      this.expandedTab = this.querySelector("[data-expanded-tab]");
      this.close = this.querySelector("[data-close]");

      this.slideCards = this.querySelectorAll("[data-slide-card]");
      this.expandedCards = this.querySelectorAll("[data-expanded-card]");

      this.initTabs();
      this.initSidebar();
    }

    // Initialize tabs by setting up event listeners
    initTabs() {
      this.bars.forEach((bar, index) => {
        bar.addEventListener("click", () => this.activateTab(index));
      });
    }

    // Initialize sidebar and related functionality
    initSidebar() {
      this.slideCards.forEach((card, index) => {
        card.addEventListener("click", () => {
          console.log("Card clicked:", index);
          this.sidebar.toggleAttribute("data-active");
          document.body.style.overflow = "hidden"; // Prevent scrolling when sidebar is open
          setTimeout(() => {
            this.expandedTab.toggleAttribute("data-active");
            this.expandedCards[index].toggleAttribute("data-active");
          }, 200);
        });
      });

      this.close?.addEventListener("click", () => {
        this.closeSidebar();
      });

      this.sidebar?.addEventListener("click", (event) => {
        if (event.target === this.sidebar) {
          this.closeSidebar();
        }
      });
    }

    closeSidebar() {
      this.sidebar.removeAttribute("data-active");
      this.expandedTab.removeAttribute("data-active");
      this.expandedCards.forEach((card) => card.removeAttribute("data-active"));
      document.body.style.overflow = ""; // Restore scrolling when sidebar is closed
    }

    // Activate a specific tab and deactivate others
    activateTab(index) {
      this.bars.forEach((bar, i) =>
        bar.toggleAttribute("data-active", i === index),
      );
      this.tabs.forEach((tab, i) =>
        tab.toggleAttribute("data-active", i === index),
      );
      this.tabs[index].querySelector("simple-slider")?.setupSlider();
    }
  }

  customElements.define("simple-tabs", SimpleTabs);
}

function debounceSliderResize(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get("simple-slider")) {
  class SimpleSlider extends HTMLElement {
    constructor() {
      super();
      this.setupSlider();

      this._onResize = debounceSliderResize(() => this.setupSlider(), 150);
      window.addEventListener("resize", this._onResize);
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this._onResize);
    }

    setupSlider() {
      this.sliderList = this.querySelector("[data-list]");
      this.slideLeft = this.querySelector("[data-slide-left]");
      this.slideRight = this.querySelector("[data-slide-right]");
      this.progressBar = this.querySelector("[data-progress-bar]");

      this.slide = this.sliderList?.children[0] || null;
      this.slideScrollPath = Math.round(
        this.sliderList.scrollWidth / this.sliderList?.children.length,
      );
      this.currentIndex = 0;
      this.size = this.sliderList.children.length;

      this.isPointerDown = false;
      this.startX = 0;
      this.scrollLeftStart = 0;
      this.dragged = false; // Track whether dragging occurred
      this.hasScrolled = false; // Ensure only one scroll per click

      this.isSafari =
        navigator.userAgent.includes("Safari") &&
        !navigator.userAgent.includes("Chrome");

      if (this.isSafari) {
        this.sensitivity = 1.5; // Adjust sensitivity for Safari
      } else {
        this.sensitivity = 1;
      }

      this.setupEventListeners();
      this.setupObservers();
      this.visibilityNavigation();
      this.visibilityProgressBar();
      this.updateControlsVisibility();
    }

    updateControlsVisibility() {
      const slidesPerView = this.getSlidesPerView();
      const needsSlider = this.size > slidesPerView;
      const display = needsSlider ? "" : "none";
      if (this.slideLeft) this.slideLeft.style.display = display;
      if (this.slideRight) this.slideRight.style.display = display;
      if (this.progressBar)
        this.progressBar.style.display = needsSlider ? "" : "none";
    }

    setupEventListeners() {
      this.slideLeft?.addEventListener("click", () => this.scrollSliderLeft());
      this.slideRight?.addEventListener("click", () =>
        this.scrollSliderRight(),
      );

      if (window.innerWidth >= 900) {
        // Pointer (Mouse & Touch) drag support
        this.sliderList.addEventListener(
          "pointerdown",
          this.onPointerDown.bind(this),
        );
        this.sliderList.addEventListener(
          "pointermove",
          this.onPointerMove.bind(this),
        );
        this.sliderList.addEventListener(
          "pointerup",
          this.onPointerUp.bind(this),
        );
        this.sliderList.addEventListener(
          "pointerleave",
          this.onPointerUp.bind(this),
        );

        // Prevent link navigation on drag
        this.sliderList.querySelectorAll("a").forEach((link) => {
          link.addEventListener("click", (event) => {
            if (this.dragged) {
              event.preventDefault();
            }
          });
        });
      }

      if (this.progressBar) {
        [...this.progressBar.children].forEach((line, index) => {
          line.addEventListener("click", () => {
            this.scrollToSlide(index);
          });
        });
      }
    }

    onPointerDown(event) {
      this.isPointerDown = true;
      this.startX = event.pageX;
      this.scrollLeftStart = this.sliderList.scrollLeft;
      this.dragged = false; // Reset dragged state on new drag
      this.hasScrolled = false; // Allow scrolling again
      this.sliderList.style.cursor = "grabbing"; // Change cursor to indicate dragging
      event.preventDefault(); // Prevent unintended selection
    }

    onPointerMove(event) {
      if (!this.isPointerDown || this.hasScrolled) return;
      const moveX = (event.pageX - this.startX) * this.sensitivity;

      if (Math.abs(moveX) > 2) {
        // Reduced sensitivity for quicker response
        this.dragged = true;
      }

      if (Math.abs(moveX) > 20) {
        // Set threshold for one-time scroll
        if (moveX > 0) {
          this.scrollSliderLeft();
        } else {
          this.scrollSliderRight();
        }
        this.hasScrolled = true; // Prevent further scrolling until next click
      }
    }

    onPointerUp() {
      this.isPointerDown = false;
      this.sliderList.style.cursor = "grab"; // Reset cursor
    }

    scrollSliderLeft() {
      this.currentIndex = Math.max(0, this.currentIndex - 1);
      this.sliderList.scrollLeft -= this.slideScrollPath;
      this.visibilityNavigation();
      this.visibilityProgressBar();
    }

    scrollSliderRight() {
      this.currentIndex = Math.min(this.size - 1, this.currentIndex + 1);
      this.sliderList.scrollLeft += this.slideScrollPath;
      this.visibilityNavigation();
      this.visibilityProgressBar();
    }

    setupObservers() {
      const left = this.slide.offsetLeft;
      const right =
        this.sliderList.clientWidth - left - this.slide.clientWidth - 30;
      const threshold = 0.5;
      const rootMargin = `0% ${-Math.max(0, right)}px 0% ${-Math.max(0, left)}px`;
      const observerOptions = {
        threshold,
        rootMargin,
      };

      const sliderScrollObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.currentIndex = parseInt(entry.target.dataset.index, 10);
            this.visibilityNavigation();
            this.visibilityProgressBar();
            // console.log("Current index:", this.currentIndex, 'Slides per view:', this.getSlidesPerView());
          }
        });
      }, observerOptions);

      [...this.sliderList.children].forEach((slide, index) => {
        slide.dataset.index = index;
        sliderScrollObserver.observe(slide);
      });
    }

    getSlidesPerView() {
      const sliderContainer = this.sliderList;
      const slides = [...this.sliderList.children];

      if (!sliderContainer || slides.length === 0) return 1;

      const containerWidth = sliderContainer.clientWidth;
      const slideWidth = slides[0].clientWidth;
      const firstSlideOffset = slides[0].offsetLeft; // Distance from the left edge of the container
      const gap = 20; // Fixed gap between slides

      // Subtract the gap for all but the last slide
      const adjustedWidth = containerWidth - firstSlideOffset;

      // Calculate the number of slides per view, including the gap
      const slidesPerView = Math.max(
        1,
        Math.floor((adjustedWidth + gap) / (slideWidth + gap)),
      );

      return slidesPerView;
    }

    visibilityNavigation() {
      if (this.currentIndex === 0) {
        this.slideLeft?.setAttribute("data-active", "false");
      } else {
        this.slideLeft?.setAttribute("data-active", "true");
      }

      if (this.currentIndex >= this.size - this.getSlidesPerView()) {
        this.slideRight?.setAttribute("data-active", "false");
      } else {
        this.slideRight?.setAttribute("data-active", "true");
      }
    }

    visibilityProgressBar() {
      const slidesPerView = this.getSlidesPerView();
      const totalScreens = Math.ceil(this.size / slidesPerView);

      if (this.progressBar) {
        // Ensure the progress bar has the correct number of children
        while (this.progressBar.children.length < totalScreens) {
          const line = document.createElement("div");
          line.classList.add("simple-slider__line");
          this.progressBar.appendChild(line);
        }
        while (this.progressBar.children.length > totalScreens) {
          this.progressBar.removeChild(this.progressBar.lastChild);
        }

        // Update active state for the current screen
        [...this.progressBar.children].forEach((line) =>
          line?.removeAttribute("data-active"),
        );
        const activeLineIndex = Math.floor(this.currentIndex / slidesPerView);
        [...this.progressBar.children][activeLineIndex]?.setAttribute(
          "data-active",
          "",
        );
      }
    }

    scrollToSlide(index) {
      if (index < 0 || index >= this.sliderList.children.length) return;
      this.currentIndex = index * this.getSlidesPerView();
      this.sliderList.scrollTo({
        left: this.slideScrollPath * index * this.getSlidesPerView(),
        behavior: "smooth",
      });
      this.visibilityNavigation();
      this.visibilityProgressBar();
    }
  }

  customElements.define("simple-slider", SimpleSlider);
}

if (!customElements.get("show-more")) {
  class ShowMore extends HTMLElement {
    constructor() {
      super();
      this.contents = this.querySelectorAll("[data-content]");
      this.buttons = this.querySelectorAll("[data-button]");
      this.initShowMore();
    }

    // Initialize event listeners for buttons
    initShowMore() {
      this.buttons.forEach((button, index) => {
        button.addEventListener("click", (e) =>
          this.handleButtonClick(e, index),
        );
      });
    }

    // Handle button click to toggle content visibility
    handleButtonClick(event, index) {
      event.preventDefault();
      this.updateActiveStates(index);
    }

    // Update active states for contents and buttons
    updateActiveStates(index) {
      this.contents.forEach((content, i) =>
        content.toggleAttribute("data-active", i === index),
      );
      this.buttons.forEach((button, i) =>
        button.toggleAttribute("data-active", i !== index),
      );
    }
  }

  customElements.define("show-more", ShowMore);
}

if (!customElements.get("steps-slider")) {
  class StepsSlider extends HTMLElement {
    constructor() {
      super();

      // Elements
      this.items = this.querySelectorAll("[data-item]");
      this.track = this.querySelector("[data-track]");
      this.list = this.querySelector("[data-list]");
      this.thumb = this.querySelector("[data-thumb]");
      this.prev = this.querySelector("[data-prev]");
      this.next = this.querySelector("[data-next]");

      this.size = this.items.length;
      this.thumbStartX = 0;
      this.dragStartX = 0;
      this.isDragging = false;

      this.init();
    }

    init() {
      this.initSliderClicks();
      this.initThumbDrag();

      // Add navigation button events
      this.prev?.addEventListener("click", () => {
        this.navigateStep(-1);
      });
      this.next?.addEventListener("click", () => {
        this.navigateStep(1);
      });

      // Add intersection observer for mobile
      if (window.innerWidth <= 750) {
        this.initIntersectionObserver();
      }

      this.initThumbPosition();
    }

    // Position the thumb on the initially active step
    initThumbPosition() {
      if (!this.thumb || !this.track) return;

      let activeIndex = Array.from(this.items).findIndex((item) =>
        item.hasAttribute("data-active"),
      );
      if (activeIndex === -1) activeIndex = 0;

      const place = () => {
        this.moveThumbToIndex(activeIndex);
        // moveThumbToIndex enables a transition; override it in the same frame
        // so the initial placement snaps instead of animating from translateX(0).
        this.thumb.style.transition = "none";
        // Restore on the next frame so later moves animate normally.
        requestAnimationFrame(() => {
          this.thumb.style.transition = "";
        });
      };

      requestAnimationFrame(place);

      // The thumb width is auto and the image is lazy-loaded, so re-place
      // once the image has loaded and its real size is known.
      const img = this.thumb.querySelector("img");
      if (img && !img.complete) {
        img.addEventListener("load", place, { once: true });
      }
    }

    // Set up click events on steps
    initSliderClicks() {
      this.items.forEach((item, index) => {
        item.addEventListener("click", () => {
          this.setActiveIndex(index);
          this.moveThumbToIndex(index);
        });
      });
    }

    // Handle thumb dragging
    initThumbDrag() {
      if (!this.thumb || !this.track) return;

      const getTrackWidth = () => this.track.getBoundingClientRect().width;
      const getThumbWidth = () => this.thumb.getBoundingClientRect().width;

      const onDown = (e) => {
        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.thumbStartX = this.getThumbTranslateX();
        this.thumb.style.transition = "none";
        document.body.style.userSelect = "none";
        this.thumb.style.cursor = "grabbing";
        this.thumb.setPointerCapture(e.pointerId);
      };

      const onMove = (e) => {
        if (!this.isDragging) return;

        const dx = e.clientX - this.dragStartX;
        const maxTranslate = getTrackWidth() - getThumbWidth();
        let newX = this.thumbStartX + dx;
        newX = Math.max(0, Math.min(newX, maxTranslate));

        this.thumb.style.transform = `translateX(${newX}px)`;

        // Find the closest item center to the thumb's center
        const thumbCenter = newX + getThumbWidth() / 2;
        let closestIndex = 0;
        let minDist = Infinity;
        this.items.forEach((item, i) => {
          const itemRect = item.getBoundingClientRect();
          const trackRect = this.track.getBoundingClientRect();
          const itemCenter =
            itemRect.left - trackRect.left + itemRect.width / 2;
          const dist = Math.abs(itemCenter - thumbCenter);
          if (dist < minDist) {
            minDist = dist;
            closestIndex = i;
          }
        });
        this.setActiveIndex(closestIndex);
      };

      const onUp = (e) => {
        if (!this.isDragging) return;
        this.isDragging = false;

        this.thumb.style.transition = "";
        document.body.style.userSelect = "";
        this.thumb.style.cursor = "grab";
        this.thumb.releasePointerCapture(e.pointerId);

        // Snap to the closest item center
        const currentX = this.getThumbTranslateX();
        const thumbWidth = getThumbWidth();
        const thumbCenter = currentX + thumbWidth / 2;
        let closestIndex = 0;
        let minDist = Infinity;
        this.items.forEach((item, i) => {
          const itemRect = item.getBoundingClientRect();
          const trackRect = this.track.getBoundingClientRect();
          const itemCenter =
            itemRect.left - trackRect.left + itemRect.width / 2;
          const dist = Math.abs(itemCenter - thumbCenter);
          if (dist < minDist) {
            minDist = dist;
            closestIndex = i;
          }
        });
        this.moveThumbToIndex(closestIndex);
      };

      this.thumb.addEventListener("pointerdown", onDown);
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);

      this.thumb.style.cursor = "grab";
    }

    // Get current thumb X translation
    getThumbTranslateX() {
      const style = window.getComputedStyle(this.thumb);
      const matrix = new DOMMatrixReadOnly(style.transform);
      return matrix.m41 || 0;
    }

    // Calculate index from current position
    calcIndexFromTranslate(translateX, maxTranslate) {
      const percent = translateX / maxTranslate;
      return Math.round(percent * (this.size - 1));
    }

    // Mark the active step
    setActiveIndex(index) {
      this.items.forEach((el, i) =>
        el.toggleAttribute("data-active", i === index),
      );
      this.updateNavButtons(index); // Update nav button visibility
    }

    // Add this method to StepsSlider
    updateNavButtons(index) {
      if (this.prev) {
        if (index === 0) {
          this.prev.removeAttribute("data-active");
        } else {
          this.prev.setAttribute("data-active", "");
        }
      }
      if (this.next) {
        if (index === this.size - 1) {
          this.next.removeAttribute("data-active");
        } else {
          this.next.setAttribute("data-active", "");
        }
      }
    }

    // Move the thumb to a specific step (centered on item)
    moveThumbToIndex(index) {
      if (!this.track || !this.thumb || !this.items[index]) return;

      const trackRect = this.track.getBoundingClientRect();
      const thumbRect = this.thumb.getBoundingClientRect();
      const itemRect = this.items[index].getBoundingClientRect();

      this.thumb.style.transition = "transform 0.2s";

      if (window.innerWidth > 750) {
        const itemCenter = itemRect.left - trackRect.left + itemRect.width / 2;
        const newX = itemCenter - thumbRect.width / 2;
        const maxTranslate = trackRect.width - thumbRect.width;
        this.thumb.style.transform = `translateX(${Math.max(0, Math.min(newX, maxTranslate))}px)`;
      } else {
        const maxTranslate = trackRect.width - thumbRect.width;
        const segmentCenter =
          ((index + 0.5) * trackRect.width) / this.size;
        const newX = segmentCenter - thumbRect.width / 2;
        this.thumb.style.transform = `translateX(${Math.max(0, Math.min(newX, maxTranslate))}px)`;
      }
    }

    // Initialize Intersection Observer for mobile
    initIntersectionObserver() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Set active by index or id
              const index = Array.from(this.items).indexOf(entry.target);
              if (index !== -1) {
                this.setActiveIndex(index);
                this.moveThumbToIndex(index);
              }
            }
          });
        },
        {
          root: this.list,
          threshold: 0.5, // Adjust as needed
        },
      );

      this.items.forEach((item) => observer.observe(item));
    }

    // Add this new method inside StepsSlider
    navigateStep(direction) {
      // Find the current active index
      let currentIndex = Array.from(this.items).findIndex((item) =>
        item.hasAttribute("data-active"),
      );
      if (currentIndex === -1) currentIndex = 0;
      let newIndex = currentIndex + direction;
      newIndex = Math.max(0, Math.min(newIndex, this.size - 1));
      this.setActiveIndex(newIndex);
      this.moveThumbToIndex(newIndex);
    }
  }

  customElements.define("steps-slider", StepsSlider);
}
